/**
 * Frontend Authorization Utilities for AirReserve
 *
 * This module provides client-side permission checking with:
 * - Integration with our backend permission system
 * - React hooks for permission-based rendering
 * - Type-safe permission checking
 * - Consistent authorization patterns across components
 *
 * IMPORTANT: Frontend authorization is for UI purposes only.
 * All security enforcement happens on the backend via tRPC procedures.
 */

import { useMemo } from 'react';
import React from 'react';
import { type LogtoContext } from '@logto/next';
import { PERMISSIONS, LEGACY_SCOPE_MAPPING } from './permissions';

// =============================================================================
// CORE FRONTEND PERMISSION CHECKING
// =============================================================================

/**
 * Frontend-safe permission checking
 * This mimics our backend hasPermission function but works with frontend data
 *
 * SECURITY NOTE: This is for UI rendering only - never rely on frontend
 * authorization for security. All security is enforced on the backend.
 *
 * @param userContext - LogtoContext from the authenticated user
 * @param requiredPermission - The permission to check for
 * @returns true if user appears to have the permission (UI purposes only)
 */
export function hasPermissionFrontend(
  userContext: LogtoContext | null | undefined,
  requiredPermission: string
): boolean {
  // Handle unauthenticated users
  if (!userContext?.isAuthenticated || !userContext.scopes) {
    return false;
  }

  const userScopes = userContext.scopes;

  // Direct scope check
  if (userScopes.includes(requiredPermission)) {
    return true;
  }

  // Simple role hierarchy check (frontend-safe version)
  // This is a simplified version of our backend hierarchy for UI purposes
  if (hasRoleInheritanceFrontend(userScopes, requiredPermission)) {
    return true;
  }

  // Backward compatibility check
  if (hasLegacyCompatibilityFrontend(userScopes, requiredPermission)) {
    return true;
  }

  return false;
}

/**
 * Simplified role inheritance for frontend use
 * This provides basic hierarchy checking without the full backend complexity
 */
function hasRoleInheritanceFrontend(
  userScopes: string[],
  requiredPermission: string
): boolean {
  // Global admin has all permissions
  if (userScopes.includes(PERMISSIONS.GLOBAL_ADMIN)) {
    return true;
  }

  // System admin has all permissions
  if (userScopes.includes(PERMISSIONS.SYSTEM_ADMIN)) {
    return true;
  }

  // Domain admin checks
  const domainAdminMappings: Record<string, string[]> = {
    [PERMISSIONS.BOOKINGS_ADMIN]: [
      PERMISSIONS.BOOKINGS_WRITE,
      PERMISSIONS.BOOKINGS_READ,
      PERMISSIONS.BOOKINGS_WRITE_OWN,
      PERMISSIONS.BOOKINGS_READ_OWN,
    ],
    [PERMISSIONS.FINANCES_ADMIN]: [
      PERMISSIONS.FINANCES_WRITE,
      PERMISSIONS.FINANCES_READ,
      PERMISSIONS.FINANCES_READ_OWN,
    ],
    [PERMISSIONS.AIRCRAFT_ADMIN]: [
      PERMISSIONS.AIRCRAFT_WRITE,
      PERMISSIONS.AIRCRAFT_READ,
      PERMISSIONS.AIRCRAFT_MAINTENANCE,
    ],
    [PERMISSIONS.USERS_ADMIN]: [
      PERMISSIONS.USERS_WRITE,
      PERMISSIONS.USERS_READ,
      PERMISSIONS.USERS_WRITE_OWN,
      PERMISSIONS.USERS_READ_OWN,
    ],
    [PERMISSIONS.FLIGHTLOGS_ADMIN]: [
      PERMISSIONS.FLIGHTLOGS_WRITE,
      PERMISSIONS.FLIGHTLOGS_READ,
      PERMISSIONS.FLIGHTLOGS_WRITE_OWN,
      PERMISSIONS.FLIGHTLOGS_READ_OWN,
    ],
  };

  // Check if user has admin role that grants the required permission
  for (const [adminRole, grantedPermissions] of Object.entries(
    domainAdminMappings
  )) {
    if (
      userScopes.includes(adminRole) &&
      grantedPermissions.includes(requiredPermission)
    ) {
      return true;
    }
  }

  // Write permissions include read permissions
  const writeToReadMappings: Record<string, string[]> = {
    [PERMISSIONS.BOOKINGS_WRITE]: [
      PERMISSIONS.BOOKINGS_READ,
      PERMISSIONS.BOOKINGS_READ_OWN,
    ],
    [PERMISSIONS.FINANCES_WRITE]: [
      PERMISSIONS.FINANCES_READ,
      PERMISSIONS.FINANCES_READ_OWN,
    ],
    [PERMISSIONS.AIRCRAFT_WRITE]: [PERMISSIONS.AIRCRAFT_READ],
    [PERMISSIONS.USERS_WRITE]: [
      PERMISSIONS.USERS_READ,
      PERMISSIONS.USERS_READ_OWN,
    ],
    [PERMISSIONS.FLIGHTLOGS_WRITE]: [
      PERMISSIONS.FLIGHTLOGS_READ,
      PERMISSIONS.FLIGHTLOGS_READ_OWN,
    ],
  };

  for (const [writePermission, readPermissions] of Object.entries(
    writeToReadMappings
  )) {
    if (
      userScopes.includes(writePermission) &&
      readPermissions.includes(requiredPermission)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Legacy compatibility checking for frontend
 */
function hasLegacyCompatibilityFrontend(
  userScopes: string[],
  requiredPermission: string
): boolean {
  // Check if user has legacy scope that maps to required permission
  for (const [legacyScope, newPermission] of Object.entries(
    LEGACY_SCOPE_MAPPING
  )) {
    if (
      userScopes.includes(legacyScope) &&
      newPermission === requiredPermission
    ) {
      return true;
    }
  }

  // Check reverse mapping (user has new permission, checking for legacy)
  for (const [legacyScope, newPermission] of Object.entries(
    LEGACY_SCOPE_MAPPING
  )) {
    if (
      legacyScope === requiredPermission &&
      userScopes.includes(newPermission)
    ) {
      return true;
    }
  }

  return false;
}

// =============================================================================
// REACT HOOKS FOR PERMISSION CHECKING
// =============================================================================

/**
 * React hook for permission checking
 * Provides memoized permission checking to prevent unnecessary re-renders
 *
 * @param userContext - LogtoContext from the authenticated user
 * @param requiredPermission - The permission to check for
 * @returns true if user appears to have the permission
 */
export function usePermission(
  userContext: LogtoContext | null | undefined,
  requiredPermission: string
): boolean {
  return useMemo(
    () => hasPermissionFrontend(userContext, requiredPermission),
    [userContext, requiredPermission]
  );
}

/**
 * React hook for checking multiple permissions (OR logic)
 * Returns true if user has ANY of the specified permissions
 *
 * @param userContext - LogtoContext from the authenticated user
 * @param permissions - Array of permissions to check (OR logic)
 * @returns true if user has at least one of the permissions
 */
export function useAnyPermission(
  userContext: LogtoContext | null | undefined,
  permissions: string[]
): boolean {
  return useMemo(
    () =>
      permissions.some((permission) =>
        hasPermissionFrontend(userContext, permission)
      ),
    [userContext, permissions]
  );
}

/**
 * React hook for checking multiple permissions (AND logic)
 * Returns true if user has ALL of the specified permissions
 *
 * @param userContext - LogtoContext from the authenticated user
 * @param permissions - Array of permissions to check (AND logic)
 * @returns true if user has all of the permissions
 */
export function useAllPermissions(
  userContext: LogtoContext | null | undefined,
  permissions: string[]
): boolean {
  return useMemo(
    () =>
      permissions.every((permission) =>
        hasPermissionFrontend(userContext, permission)
      ),
    [userContext, permissions]
  );
}

/**
 * React hook to get all effective permissions for debugging
 *
 * @param userContext - LogtoContext from the authenticated user
 * @returns Array of all permissions the user appears to have
 */
export function useUserPermissions(
  userContext: LogtoContext | null | undefined
): string[] {
  return useMemo(() => {
    if (!userContext?.isAuthenticated || !userContext.scopes) {
      return [];
    }

    const allPermissions = Object.values(PERMISSIONS);
    return allPermissions.filter((permission) =>
      hasPermissionFrontend(userContext, permission)
    );
  }, [userContext]);
}

// =============================================================================
// AUTHORIZATION WRAPPER COMPONENTS
// =============================================================================

/**
 * Props for permission-based wrapper components
 */
interface PermissionWrapperProps {
  children: React.ReactNode;
  userContext: LogtoContext | null | undefined;
  fallback?: React.ReactNode;
}

/**
 * Component that renders children only if user has required permission
 *
 * @param permission - Required permission
 * @param children - Content to render if authorized
 * @param userContext - LogtoContext from the authenticated user
 * @param fallback - Optional fallback content if not authorized
 */
export function PermissionGuard({
  permission,
  children,
  userContext,
  fallback = null,
}: PermissionWrapperProps & { permission: string }) {
  const hasAccess = usePermission(userContext, permission);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * Component that renders children only if user has ANY of the required permissions
 */
export function AnyPermissionGuard({
  permissions,
  children,
  userContext,
  fallback = null,
}: PermissionWrapperProps & { permissions: string[] }) {
  const hasAccess = useAnyPermission(userContext, permissions);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * Component that renders children only if user has ALL of the required permissions
 */
export function AllPermissionsGuard({
  permissions,
  children,
  userContext,
  fallback = null,
}: PermissionWrapperProps & { permissions: string[] }) {
  const hasAccess = useAllPermissions(userContext, permissions);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if user has admin access to any domain
 */
export function useIsAnyAdmin(
  userContext: LogtoContext | null | undefined
): boolean {
  const hasAnyAdminPermission = useAnyPermission(userContext, [
    PERMISSIONS.GLOBAL_ADMIN,
    PERMISSIONS.SYSTEM_ADMIN,
    PERMISSIONS.BOOKINGS_ADMIN,
    PERMISSIONS.FINANCES_ADMIN,
    PERMISSIONS.AIRCRAFT_ADMIN,
    PERMISSIONS.USERS_ADMIN,
    PERMISSIONS.FLIGHTLOGS_ADMIN,
  ]);

  return userContext?.isAuthenticated ? hasAnyAdminPermission : false;
}

/**
 * Check if user is global admin
 */
export function useIsGlobalAdmin(
  userContext: LogtoContext | null | undefined
): boolean {
  return usePermission(userContext, PERMISSIONS.GLOBAL_ADMIN);
}

/**
 * Non-hook version: Check if user has any admin permissions
 */
export function isAnyAdmin(
  userContext: LogtoContext | null | undefined
): boolean {
  if (!userContext?.isAuthenticated) return false;

  return [
    PERMISSIONS.GLOBAL_ADMIN,
    PERMISSIONS.SYSTEM_ADMIN,
    PERMISSIONS.BOOKINGS_ADMIN,
    PERMISSIONS.FINANCES_ADMIN,
    PERMISSIONS.AIRCRAFT_ADMIN,
    PERMISSIONS.USERS_ADMIN,
    PERMISSIONS.FLIGHTLOGS_ADMIN,
  ].some((permission) => hasPermissionFrontend(userContext, permission));
}

/**
 * Non-hook version: Check if user is global admin
 */
export function isGlobalAdmin(
  userContext: LogtoContext | null | undefined
): boolean {
  return hasPermissionFrontend(userContext, PERMISSIONS.GLOBAL_ADMIN);
}

/**
 * Get user's role level for display purposes
 */
export function getUserRole(
  userContext: LogtoContext | null | undefined
): string {
  if (!userContext?.isAuthenticated) return 'Guest';

  if (hasPermissionFrontend(userContext, PERMISSIONS.SYSTEM_ADMIN))
    return 'System Admin';
  if (hasPermissionFrontend(userContext, PERMISSIONS.GLOBAL_ADMIN))
    return 'Global Admin';
  if (isAnyAdmin(userContext)) return 'Domain Admin';
  return 'Member';
}
